import React, { useState } from 'react';

/* ── Design tokens ─────────────────────────────────────── */
const T = {
  bg:        '#0c0c0e',
  surface:   '#141416',
  surfaceHi: '#1c1c1f',
  border:    '#252528',
  borderHi:  '#38383d',
  text:      '#f0f0f2',
  textSub:   '#8a8a95',
  textMute:  '#4a4a52',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  blueMid:   'rgba(59,130,246,0.18)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
};

const pageStyle = {
  minHeight: '100vh',
  backgroundColor: T.bg,
  color: T.text,
  fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
};

function PageHeader({ onBack }) {
  return (
    <div style={{
      backgroundColor: 'rgba(12,12,14,0.97)',
      borderBottom: `1px solid ${T.border}`,
      backdropFilter: 'blur(16px)',
      padding: '0 60px', height: '56px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={onBack}>
        <div style={{ width: '28px', height: '28px', background: `linear-gradient(135deg, ${T.blue}, #1d4ed8)`, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🧬</div>
        <div>
          <span style={{ fontSize: '14px', fontWeight: '700', color: T.text, letterSpacing: '-0.3px' }}>
            Alpha<span style={{ color: T.blue }}>Bio</span>AI
          </span>
          <div style={{ fontSize: '10px', color: T.textMute, letterSpacing: '0.5px' }}>INTELLIGENCE PLATFORM</div>
        </div>
      </div>
      <button onClick={onBack} style={{
        backgroundColor: 'transparent', border: `1px solid ${T.border}`,
        color: T.textSub, padding: '6px 14px', borderRadius: '5px',
        cursor: 'pointer', fontSize: '12px', fontWeight: '500', transition: 'all 0.15s',
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHi; e.currentTarget.style.color = T.text; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}
      >← Back</button>
    </div>
  );
}

function Section({ title, children, last }) {
  return (
    <div style={{ marginBottom: '40px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: T.text, margin: '0 0 14px', letterSpacing: '-0.3px' }}>{title}</h2>
      <div style={{ fontSize: '15px', color: T.textSub, lineHeight: '1.85', whiteSpace: 'pre-line' }}>{children}</div>
      {!last && <div style={{ height: '1px', backgroundColor: T.border, marginTop: '40px' }} />}
    </div>
  );
}

/* ── Legal Disclaimer ───────────────────────────────────── */
export function LegalDisclaimer({ onBack }) {
  return (
    <div style={pageStyle}>
      <PageHeader onBack={onBack} />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 40px 100px' }}>

        <div style={{ marginBottom: '48px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: T.amber, marginBottom: '12px', fontFamily: "'DM Mono', monospace" }}>IMPORTANT</div>
          <h1 style={{ fontSize: '40px', fontWeight: '700', letterSpacing: '-1.5px', color: T.text, margin: '0 0 12px' }}>Legal Disclaimer</h1>
          <p style={{ color: T.textMute, fontSize: '13px', fontFamily: "'DM Mono', monospace" }}>Last updated: March 20, 2026</p>
        </div>

        <div style={{ backgroundColor: 'rgba(245,158,11,0.06)', border: `1px solid rgba(245,158,11,0.18)`, borderLeft: `3px solid ${T.amber}`, borderRadius: '6px', padding: '18px 20px', marginBottom: '44px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: T.amber, marginBottom: '6px' }}>Not Financial Advice</div>
          <div style={{ fontSize: '14px', color: T.textSub, lineHeight: '1.7' }}>
            AlphaBioAI is an information and intelligence tool, not a registered investment advisor, broker-dealer, or financial planning firm. Nothing on this platform constitutes financial, investment, legal, or tax advice.
          </div>
        </div>

        {[
          { title: 'No Investment Advice', content: `The information, AI summaries, risk scores, and insights provided by AlphaBioAI are for informational and educational purposes only. They do not constitute investment advice, recommendations, or solicitations to buy or sell any securities.\n\nYou should not make any investment decision based solely on information from AlphaBioAI. Always conduct your own due diligence and consult with a qualified financial advisor before making investment decisions.` },
          { title: 'AI-Generated Content', content: `AlphaBioAI uses artificial intelligence to generate summaries and analysis of publicly available biotech data. While we strive for accuracy, AI-generated content may contain errors, omissions, or outdated information. We make no representations or warranties about the accuracy, completeness, or timeliness of any AI-generated analysis.` },
          { title: 'Data Sources', content: `AlphaBioAI aggregates data from publicly available government sources including the FDA Drug Database, ClinicalTrials.gov, and SEC EDGAR. While these are authoritative sources, data may be delayed, incomplete, or subject to change. Always verify important information directly with official sources.` },
          { title: 'Investment Risks', content: `Biotech investing involves significant risks, including:\n\n• High volatility — biotech stocks can move dramatically on FDA decisions\n• Binary events — a single trial failure can cause a stock to lose 80%+ of its value\n• Regulatory uncertainty — FDA approval is never guaranteed\n• Market risk — even approved drugs may underperform commercially\n\nPast performance of any drug, company, or investment strategy does not guarantee future results. You may lose some or all of your investment.` },
          { title: 'Risk Ratings', content: `The Low, Medium, and High risk ratings provided by AlphaBioAI are AI-generated assessments based on publicly available information. They are not professional risk assessments and should not be relied upon as the sole basis for any investment decision. These ratings reflect general market and regulatory factors and do not account for your personal financial situation, risk tolerance, or investment objectives.` },
          { title: 'No Liability', content: `AlphaBioAI, its founders, employees, and affiliates shall not be liable for any investment losses, damages, or other financial harm resulting from your use of or reliance on any information provided through the Service. Your use of AlphaBioAI is entirely at your own risk.` },
          { title: 'Regulatory Compliance', content: `AlphaBioAI is not registered with the Securities and Exchange Commission (SEC), Financial Industry Regulatory Authority (FINRA), or any other financial regulatory body. We do not provide personalized investment advice and are not subject to the same regulatory requirements as registered investment advisors.` },
        ].map((s, i, arr) => <Section key={i} title={s.title} last={i === arr.length - 1}>{s.content}</Section>)}
      </div>
    </div>
  );
}

/* ── About Us ───────────────────────────────────────────── */
export function AboutUs({ onBack }) {
  return (
    <div style={pageStyle}>
      <PageHeader onBack={onBack} />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 40px 100px' }}>

        <div style={{ marginBottom: '52px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: T.blue, marginBottom: '12px', fontFamily: "'DM Mono', monospace" }}>COMPANY</div>
          <h1 style={{ fontSize: '40px', fontWeight: '700', letterSpacing: '-1.5px', color: T.text, margin: '0 0 16px' }}>About AlphaBioAI</h1>
          <p style={{ color: T.textSub, fontSize: '17px', lineHeight: '1.7', margin: 0 }}>
            Democratizing biotech investment intelligence for retail investors.
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '56px' }}>
          {[
            { value: '2,000+', label: 'AI Analyses', color: T.blue },
            { value: '$19/mo', label: 'Starting Price', color: T.green },
            { value: '8', label: 'Intelligence Modules', color: T.purple },
          ].map((s, i) => (
            <div key={i} style={{ backgroundColor: T.surface, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}`, textAlign: 'center' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: s.color, letterSpacing: '-1px', fontFamily: "'DM Mono', monospace" }}>{s.value}</div>
              <div style={{ fontSize: '12px', color: T.textMute, marginTop: '5px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <Section title="Our Mission">
          {`AlphaBioAI exists to level the playing field between institutional investors and retail investors in the biotech space.\n\nHedge funds and institutional investors have access to Bloomberg Terminal ($25,000/year), AlphaSense, Clarivate, and teams of analysts dedicated to monitoring the FDA pipeline. Retail investors — who make up the majority of biotech stock holders — have nothing comparable.\n\nWe're changing that. AlphaBioAI brings institutional-grade biotech intelligence to everyday investors at a price anyone can afford.`}
        </Section>

        <Section title="What We Do">
          {`AlphaBioAI continuously monitors publicly available government data sources including the FDA Drug Database, ClinicalTrials.gov, and SEC EDGAR. We use AI to transform complex regulatory and scientific data into plain-English investment insights any retail investor can understand and act on.\n\nEvery drug and clinical trial in our database receives an AI-generated SUMMARY, RISK rating with reasoning, and actionable INSIGHT — saving hours of research and making biotech investing accessible to everyone.`}
        </Section>

        <Section title="Who We Are">
          {`AlphaBioAI was founded by Nonso, a UC Berkeley alumnus who graduated from the Master of Information and Data Science (MIDS) program — one of the top AI and data science programs in the world.\n\nAt Berkeley, Nonso studied machine learning, deep learning, natural language processing, and applied AI at scale. That technical foundation is the backbone of AlphaBioAI: every AI summary, risk score, and intelligence module is built on the same principles taught at one of the world's leading research institutions.\n\nThe insight was simple but powerful: the tools used by professional biotech investors cost tens of thousands of dollars per year and are built for institutional clients — leaving retail investors with no real alternative. Using modern AI, freely available government data, and rigorous data science, AlphaBioAI bridges that gap.\n\nThis is not a side project. It's a company built with the same discipline and rigor that Berkeley demands.`}
        </Section>

        <Section title="Our Technology">
          {`AlphaBioAI is built on a modern, production-grade technology stack:\n\n• AI Analysis: Anthropic Claude — state-of-the-art large language models for biotech intelligence\n• Data: FDA API, ClinicalTrials.gov, SEC EDGAR Form 4 filings\n• Backend: Flask (Python) on Render\n• Database: Supabase (PostgreSQL)\n• Frontend: React on Vercel\n• Market Data: Polygon.io (real-time stock prices)\n• Payments: Stripe\n\nAll government data sources are free and publicly available. Our competitive advantage comes from the quality of our AI analysis, the depth of our proprietary database, and the speed at which we iterate.`}
        </Section>

        <Section title="Contact" last>
          {`We'd love to hear from you — whether you're an investor with feedback, a journalist with questions, or someone curious about what we're building.\n\nEmail: nonso@alphabioai.ai\nWebsite: alphabioai.ai`}
        </Section>
      </div>
    </div>
  );
}

/* ── Contact ────────────────────────────────────────────── */
export function Contact({ onBack }) {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:nonso@alphabioai.ai?subject=${encodeURIComponent(form.subject || 'AlphaBioAI Inquiry')}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    setSent(true);
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '6px',
    border: `1px solid ${T.border}`, backgroundColor: T.surface,
    color: T.text, fontSize: '14px', outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  };

  return (
    <div style={pageStyle}>
      <PageHeader onBack={onBack} />
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '60px 40px 100px' }}>

        <div style={{ marginBottom: '48px' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: T.blue, marginBottom: '12px', fontFamily: "'DM Mono', monospace" }}>GET IN TOUCH</div>
          <h1 style={{ fontSize: '40px', fontWeight: '700', letterSpacing: '-1.5px', color: T.text, margin: '0 0 14px' }}>Contact Us</h1>
          <p style={{ color: T.textSub, fontSize: '16px', lineHeight: '1.7', margin: 0 }}>
            Have a question, feedback, or want to learn more? We'd love to hear from you.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '44px' }}>
          {[
            { label: 'Email', value: 'nonso@alphabioai.ai' },
            { label: 'Website', value: 'alphabioai.ai' },
          ].map((item, i) => (
            <div key={i} style={{ backgroundColor: T.surface, borderRadius: '8px', padding: '18px', border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: '10px', color: T.textMute, fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '5px', fontFamily: "'DM Mono', monospace" }}>{item.label}</div>
              <div style={{ fontSize: '13px', color: T.blue, fontWeight: '500' }}>{item.value}</div>
            </div>
          ))}
        </div>

        {sent ? (
          <div style={{ backgroundColor: 'rgba(34,197,94,0.06)', border: `1px solid rgba(34,197,94,0.18)`, borderLeft: `3px solid ${T.green}`, borderRadius: '6px', padding: '28px', textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: T.green, marginBottom: '6px' }}>Message sent</div>
            <div style={{ fontSize: '13px', color: T.textSub }}>Your email client should have opened. We'll get back to you within 24 hours.</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { label: 'Your Name', key: 'name', type: 'text', placeholder: 'John Smith' },
              { label: 'Email Address', key: 'email', type: 'email', placeholder: 'john@example.com' },
              { label: 'Subject', key: 'subject', type: 'text', placeholder: 'Question about AlphaBioAI' },
            ].map(field => (
              <div key={field.key}>
                <label style={{ fontSize: '11px', color: T.textMute, fontWeight: '700', display: 'block', marginBottom: '7px', letterSpacing: '0.8px', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" }}>{field.label}</label>
                <input type={field.type} placeholder={field.placeholder} value={form[field.key]}
                  onChange={e => setForm({ ...form, [field.key]: e.target.value })} required
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = T.blue}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
              </div>
            ))}
            <div>
              <label style={{ fontSize: '11px', color: T.textMute, fontWeight: '700', display: 'block', marginBottom: '7px', letterSpacing: '0.8px', textTransform: 'uppercase', fontFamily: "'DM Mono', monospace" }}>Message</label>
              <textarea placeholder="Tell us what's on your mind..." value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })} required rows={5}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={e => e.target.style.borderColor = T.blue}
                onBlur={e => e.target.style.borderColor = T.border}
              />
            </div>
            <button type="submit" style={{
              backgroundColor: T.blue, color: 'white', border: 'none',
              padding: '12px', borderRadius: '6px', cursor: 'pointer',
              fontWeight: '600', fontSize: '14px', transition: 'all 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2563eb'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = T.blue}
            >Send Message →</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── FAQ ────────────────────────────────────────────────── */
export function FAQ({ onBack }) {
  const [open, setOpen] = useState(null);

  const faqs = [
    { q: 'What is AlphaBioAI?', a: 'AlphaBioAI is an AI-powered biotech investment intelligence platform for retail investors. We track FDA drug approvals, clinical trials, insider trading, FDA failures, earnings collisions, and more — then use AI to generate plain-English summaries, risk ratings, and investment insights across all of it.' },
    { q: 'How is this different from Bloomberg Terminal?', a: 'Bloomberg Terminal costs $25,000/year and is designed for institutional investors with dedicated finance teams. AlphaBioAI starts at $19/month and is built specifically for retail investors who want real biotech intelligence without needing a finance degree, a Bloomberg subscription, or a six-figure budget.' },
    { q: 'What intelligence modules are included?', a: 'AlphaBioAI includes 8 modules: FDA Drug Approvals, Clinical Trials, FDA Calendar (PDUFA dates with countdown timers), Watchlist, Portfolio Tracker (live prices via Polygon.io), Insider Trading (SEC Form 4 filings), FDA Failure Post-Mortems (CRL analysis), and the Earnings × FDA Collision Detector.' },
    { q: 'Is the data real-time?', a: 'Our intelligence database is updated weekly via automated pipelines pulling from the FDA API, ClinicalTrials.gov, and SEC EDGAR. Stock prices in the Portfolio Tracker are powered by Polygon.io and are live with no cold starts.' },
    { q: 'What does the risk rating mean?', a: 'Each drug and clinical trial receives a Low, Medium, or High risk rating generated by our AI. Crucially, every rating includes a written reason — not just a label. Low means established safety and market data. Medium means meaningful uncertainty exists. High means early-stage or significant regulatory risk. These are AI assessments, not financial advice.' },
    { q: 'What is Insider Trading intelligence?', a: 'We track SEC Form 4 filings — mandatory disclosures when biotech executives buy or sell their own company stock. When a CEO loads up on shares ahead of an FDA decision, that\'s a signal worth knowing. We auto-refresh this data weekly from SEC EDGAR.' },
    { q: 'What are FDA Failure Post-Mortems?', a: 'When the FDA issues a Complete Response Letter (CRL) — essentially a rejection — we analyze exactly what failed, the financial damage to the company, and what the path to resubmission looks like. These are AI-powered analyses of real CRL events, not summaries.' },
    { q: 'What is the Earnings × FDA Collision Detector?', a: 'The most volatile week in biotech: when a company has both an earnings report and an FDA decision in the same week. We automatically detect these overlaps across 46+ tracked companies. High volatility, high signal — plan your positions accordingly.' },
    { q: 'Is this financial advice?', a: 'No. AlphaBioAI provides information and intelligence tools to help you conduct your own research. Nothing on the platform constitutes financial advice. Always consult a qualified financial advisor before making investment decisions. See our Legal Disclaimer for full details.' },
    { q: 'What is the difference between Starter and Pro?', a: 'Starter ($19/mo) includes FDA Approvals, AI summaries, risk scoring, FDA Calendar, search and filters, and 10 watchlist items. Pro ($49/mo) adds Clinical Trials, Insider Trading, FDA Failures, Earnings Collider, Portfolio Tracker, unlimited watchlist, and priority support.' },
    { q: 'Can I cancel anytime?', a: 'Yes. Cancel at any time from your account settings. Cancellation takes effect at the end of your current billing period. We do not offer refunds for partial months.' },
    { q: 'Who built AlphaBioAI?', a: 'AlphaBioAI was built by Nonso, a UC Berkeley MIDS (Master of Information and Data Science) alumnus who studied machine learning, deep learning, NLP, and applied AI. The platform is built with the same rigor and technical depth that Berkeley demands — this is not a side project.' },
  ];

  return (
    <div style={pageStyle}>
      <PageHeader onBack={onBack} />
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 40px 100px' }}>

        <div style={{ marginBottom: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: T.blue, marginBottom: '12px', fontFamily: "'DM Mono', monospace" }}>SUPPORT</div>
          <h1 style={{ fontSize: '40px', fontWeight: '700', letterSpacing: '-1.5px', color: T.text, margin: '0 0 14px' }}>Frequently Asked Questions</h1>
          <p style={{ color: T.textSub, fontSize: '16px', margin: 0 }}>Everything you need to know about AlphaBioAI.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {faqs.map((faq, i) => (
            <div key={i} style={{
              backgroundColor: T.surface, borderRadius: '8px',
              border: `1px solid ${open === i ? T.blueMid : T.border}`,
              borderLeft: `2px solid ${open === i ? T.blue : T.border}`,
              overflow: 'hidden', transition: 'border-color 0.15s',
            }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: '100%', padding: '18px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: open === i ? T.text : '#c8c8d0' }}>{faq.q}</span>
                <span style={{ color: open === i ? T.blue : T.textMute, fontSize: '16px', transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'rotate(0)', flexShrink: 0, marginLeft: '16px' }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding: '0 20px 18px', fontSize: '14px', color: T.textSub, lineHeight: '1.8' }}>{faq.a}</div>
              )}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '52px', padding: '36px', backgroundColor: T.surface, borderRadius: '8px', border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: T.text, marginBottom: '6px' }}>Still have questions?</div>
          <div style={{ fontSize: '13px', color: T.textSub, marginBottom: '20px' }}>We're here to help. Reach out and we'll respond within 24 hours.</div>
          <a href="mailto:nonso@alphabioai.ai" style={{
            display: 'inline-block', backgroundColor: T.blue, color: 'white',
            textDecoration: 'none', padding: '10px 24px', borderRadius: '6px',
            fontWeight: '600', fontSize: '13px', transition: 'background 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#2563eb'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = T.blue}
          >Contact Support →</a>
        </div>
      </div>
    </div>
  );
}